/**
 * Page /cee/inbox — boite a reconcilier (PJ non rattachees).
 *
 * Liste les unmatched_attachment + low_confidence_classification non resolus.
 * Chaque ligne (InboxRow) permet :
 *  - Affecter au dossier (autocomplete client x mandataire)
 *  - Reclasser (select doc_type)
 *  - Ignorer (spam → resolved=true)
 *
 * Mock-first : si endpoints Track A/B pas encore deployes, on degrade en
 * "Aucun item" + bandeau d info.
 */

import { useState, useEffect, useCallback } from 'react';
import * as inboxApi from '../api/ceeInbox.js';
import InboxRow from './components/InboxRow.jsx';

export default function CeeInboxPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const list = await inboxApi.getInbox();
      setItems(Array.isArray(list) ? list : []);
    } catch (err) {
      // Mock-first : si endpoint pas dispo, on affiche un message clair sans bloquer
      setError(err.message || 'Inbox indisponible (Track A/B pas encore deployee)');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  /** @param {string} id @param {() => Promise<unknown>} fn */
  const wrap = async (id, fn) => {
    setBusyId(id);
    try {
      await fn();
      await load();
    } catch (err) {
      setError(err.message || 'Action echouee');
    } finally {
      setBusyId(null);
    }
  };

  const handleAffect = (eventId, dossierId, docType) => wrap(eventId, () =>
    inboxApi.affectAttachment(eventId, { dossierId, docType }));
  const handleReclassify = (eventId, docType) => wrap(eventId, () =>
    inboxApi.reclassifyAttachment(eventId, docType));
  const handleIgnore = (eventId) => wrap(eventId, () => inboxApi.ignoreAttachment(eventId));

  const totalUnresolved = items.length;
  const alertOver10 = totalUnresolved > 10;

  return (
    <div className="animate-fade-in">
      <div className="flex items-start justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold text-[#0f172a]">Boite a reconcilier</h1>
          <p className="text-sm text-[#64748b] mt-1">
            Pieces jointes recues sur transition@ qui n ont pas ete rattachees automatiquement
            a un dossier (matching client incertain ou classification basse confiance).
          </p>
        </div>
        <div className={`text-sm font-bold px-3 py-1.5 rounded-lg ${
          alertOver10 ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700'
        }`}>
          {totalUnresolved} item{totalUnresolved > 1 ? 's' : ''}
        </div>
      </div>

      {alertOver10 && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 mb-4 text-sm">
          Plus de 10 PJ en attente de reconciliation. Pense a traiter avant accumulation.
        </div>
      )}

      {error && (
        <div className="bg-orange-50 border border-orange-200 text-orange-800 rounded-xl px-4 py-3 mb-4 text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-10">
          <div className="w-7 h-7 border-3 border-gray-200 border-t-blue-500 rounded-full animate-spin mx-auto" />
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-xl p-10 text-center text-[#64748b]">
          Aucun item a reconcilier. Tout est rattache automatiquement.
        </div>
      ) : (
        <div>
          {items.map((it) => (
            <InboxRow
              key={it.id}
              item={it}
              busy={busyId === it.id}
              onAffect={handleAffect}
              onReclassify={handleReclassify}
              onIgnore={handleIgnore}
            />
          ))}
        </div>
      )}
    </div>
  );
}
